<template>
  <layout-page is-not-scrollable hide-copyright>
    <div class="inbox_layout">
      <!--Filters-->
      <div class="inbox_layout--filters flex flex-row-reverse">
        <table-buttons
          show-presets
          show-filters-button
          hide-apply-filter-button
          :filter-count="filterCount"
          :presets="inboxPresets"
          :default-preset="defaultInboxPreset"
          :do-filters-reset="resetFilters"
          :do-manage-presets="managePreset"
          @presetChanged="changePreset"
        >
          <template v-slot:filters>
            <inbox-filters
              :user-query="userQuery"
              filter-name="inbox"
            />
          </template>
        </table-buttons>
      </div>

      <!--Menu-->
      <div class="inbox_layout--menu">
        <tabs-horizontal
          v-model="currentTab"
          @input="changeTab"
        >
          <b-tab-item value="all" label="All">
            <section-box has-no-padding>
              <template v-if="allItems.length">
                <lazy-render
                  v-for="(item, index) in allItems"
                  :key="item.client_id"
                  :min-height="68"
                  :unrender="true"
                >
                  <inbox-item-desktop
                    :data="item"
                    :itemIndex="index"
                    :viewed-by="activeClientId === item.client_id ? viewedBy : []"
                    :is-active="activeClientId === item.client_id"
                    @updateActive="setActive"
                    @markedDone="setActive($event, true)"
                  ></inbox-item-desktop>
                </lazy-render>
              </template>

              <div v-else class="inbox_menu--empty">
                <h4 class="inbox_menu--empty--heading">No Inbox Items</h4>
                <p class="inbox_menu--empty--message">This is where all new leads and communications will show up.</p>
              </div>
            </section-box>
          </b-tab-item>

          <b-tab-item value="leads">
            <template #header>
              <span>
                New Leads
                <badge-default :count="newLeads.length"></badge-default>
              </span>
            </template>

            <section-box has-no-padding>
              <template v-if="newLeads.length">
                <lazy-render
                  v-for="(item, index) in newLeads"
                  :key="item.client_id"
                  :min-height="68"
                  :unrender="true"
                >

                  <inbox-item-desktop
                    :data="item"
                    :itemIndex="index"
                    :viewed-by="activeClientId === item.client_id ? viewedBy : []"
                    :is-active="activeClientId === item.client_id"
                    @updateActive="setActive"
                    @markedDone="setActive($event, true)"
                  ></inbox-item-desktop>
                </lazy-render>
              </template>

              <div v-else class="inbox_menu--empty">
                <h4 class="inbox_menu--empty--heading">No New Leads</h4>
                <p class="inbox_menu--empty--message">This is where all new leads created by calls/texts/webhooks will
                  show up.</p>
              </div>
            </section-box>
          </b-tab-item>

          <b-tab-item value="responses">
            <template #header>
              <span>
                Responses
                <badge-default :count="inboundComms.length"></badge-default>
              </span>
            </template>

            <section-box has-no-padding>
              <template v-if="inboundComms.length">
                <lazy-render
                  v-for="(item, index) in inboundComms"
                  :key="item.client_id"
                  :min-height="68"
                  :unrender="true"
                >
                  <inbox-item-desktop
                    :data="item"
                    :itemIndex="index"
                    :viewed-by="activeClientId === item.client_id ? viewedBy : []"
                    :is-active="activeClientId === item.client_id"
                    @updateActive="setActive"
                    @markedDone="setActive($event, true)"
                  ></inbox-item-desktop>
                </lazy-render>
              </template>

              <div v-else class="inbox_menu--empty">
                <h4 class="inbox_menu--empty--heading">No Responses</h4>
                <p class="inbox_menu--empty--message">This is where all new communications from existing contacts in
                  your account will show up.</p>
              </div>
            </section-box>
          </b-tab-item>
        </tabs-horizontal>
      </div>

      <!--Content-->
      <div class="inbox_layout--content">
        <inbox-content
          v-if="activeClientId && !loading"
          :type="activeType"
        ></inbox-content>

        <div v-else-if="loading" class="inbox_content--empty">
          <p class="inbox_content--loading--message">Getting Inbox information...</p>
        </div>

        <div v-else-if="allItems.length === 0" class="inbox_content--empty">
          <p class="inbox_content--empty--message">Congratulations! You've achieved inbox zero</p>
        </div>

        <div v-else class="inbox_content--empty">
          <p class="inbox_content--empty--message">Choose an item in the menu to the left</p>
        </div>
      </div>
    </div>

    <modal-container>
      <modal-default
        ref="manageInboxPresetsModal"
        title="Manage Presets"
        is-wide
      >
        <user-default-preset-form/>

        <b-tabs
          v-model="currentPresetTab"
          class="tabs_vertical_modal"
          vertical
          expanded
          :animated="false"
          @input="handlePresetTabChange"
        >
          <b-tab-item
            value="0"
            label="New Preset"
          >
            <preset-form
              v-if="currentPresetTab === 0"
              store-module="inbox"
              :preset-id="activePresetId"
              hide-columns
            />
          </b-tab-item>

          <b-tab-item
            v-for="(preset, index) in inboxPresets"
            :key="preset.id"
            :value="String(index) + 1"
            :label="preset.label"
          >
            <preset-form
              v-if="currentPresetTab === (index + 1)"
              store-module="inbox"
              :preset-id="activePresetId"
              hide-columns
              @deleted="handlePresetTabChange(0)"
            />
          </b-tab-item>
        </b-tabs>
      </modal-default>
    </modal-container>
  </layout-page>
</template>

<script>
import {ENUMS} from "../../services/enums";
import InboxItemDesktop from "./components/InboxItemDesktop";
import InboxContent from "./components/InboxContent";
import InboxFilters from "./components/InboxFilters";
import {joinPresenceChannel, leavePresenceChannel} from "../../services/pusher";
import LazyRender from "../../components/LazyRender";
import TableButtons from "../../components/table/TableButtons.vue";
import ContactsFilter from "../contacts/components/ContactsFilters.vue";
import PresetForm from "../../forms/PresetForm.vue";
import UserDefaultPresetForm from "../../forms/UserDefaultPresetForm.vue";

export default {
  name: "InboxPage",
  metaInfo() {
    return {
      title: this.metaTitle,
    };
  },
  components: {
    UserDefaultPresetForm, PresetForm,
    ContactsFilter,
    TableButtons,
    LazyRender,
    InboxFilters,
    InboxContent,
    InboxItemDesktop,
  },
  data() {
    return {
      loading: true,
      currentTab: 'all',
      activeClientId: null,
      activeType: null,
      viewedBy: [],
      userQuery: null,
      currentPresetTab: 0,
      activePresetId: 0
    };
  },
  beforeMount() {
    this.userQuery = this.$route.query.user;
  },
  mounted() {
    const tabParam = this.$route.params.tab;

    if (tabParam) {
      this.currentTab = tabParam;
    }

    this.changeTab();
  },
  destroyed() {
    this.$store.dispatch("activeInboxLeadItem/tearDown");
    this.$store.dispatch("activeInboxInboundCommItem/tearDown");
    leavePresenceChannel("viewedByInbox", this.activeClientId);
  },
  computed: {
    metaTitle() {
      switch (this.currentTab) {
        case "all":
          return "Inbox";
        case "leads":
          return "New Leads";
        case "responses":
          return "Responses";
        default:
          return "Inbox";
      }
    },
    allItems() {
      return this.$store.getters["inbox/items"];
    },
    newLeads() {
      return this.allItems.filter(item => item.inbox_type === ENUMS.InboxTypeEnum.NewLead.value);
    },
    inboundComms() {
      return this.allItems.filter(item => item.inbox_type === ENUMS.InboxTypeEnum.InboundComm.value);
    },
    inboxPresets() {
      return this.$store.getters.inboxTableConfigurations
    },
    defaultInboxPreset() {
      return this.inboxPresets.find(preset => preset.id === this.$store.getters.authUser.default_inbox_preset_id)
    },
    filterCount() {
      return this.$store.getters["inbox/filterCount"]
    },
  },
  watch: {
    newLeads(newValue, oldValue) {
      // Doing this because if a lead is deleted we need to set the activeClientId to null
      // if the activeClientId is currently the lead id that was deleted
      const newLeadsArray = newValue.filter(lead => lead.client_id === this.activeClientId);

      if (this.currentTab === "leads") {
        if (newLeadsArray.length === 0) this.activeClientId = null;
      }
    }
  },
  methods: {
    setURL() {
      const currentQuery = this.$route.query;
      this.$router.replace({params: {tab: this.currentTab}, query: currentQuery});
    },
    changeTab() {
      let item = null;
      this.activeClientId = null;

      this.setURL();

      // setup default preset
      this.$store.dispatch("inbox/setup")

      this.$store.dispatch("activeInboxLeadItem/tearDown");
      this.$store.dispatch("activeInboxInboundCommItem/tearDown");

      if (this.currentTab === "all") {
        if (this.allItems.length) item = this.allItems[0];

        this.$store.commit("inbox/setTab", "all");
      }

      if (this.currentTab === "leads") {
        if (this.newLeads.length) item = this.newLeads[0];

        this.$store.commit("inbox/setTab", "leads");
      }

      if (this.currentTab === "responses") {
        if (this.inboundComms.length) item = this.inboundComms[0];

        this.$store.commit("inbox/setTab", "responses");
      }

      if (this.activeClientId === null) {
        this.setActive(item);
      }
    },
    setActive(item, isDone = false) {
      this.loading = true;

      // If member of a channel then leave before joining another
      // This must be done first because this.activeClientId may be changed below
      if (this.activeClientId) {
        leavePresenceChannel("viewedByInbox", this.activeClientId);
      }

      let nextItem = null;

      if (isDone) {
        // Get next item
        const index = item.itemIndex ? item.itemIndex : 0;
        const nextIndex = index + 1;

        if (this.currentTab === 'all') {
          nextItem = this.allItems[nextIndex]
        }

        if (this.currentTab === "leads") {
          nextItem = this.newLeads[nextIndex]
        }

        if (this.currentTab === "responses") {
          nextItem = this.inboundComms[nextIndex]
        }
      } else {
        nextItem = item
      }

      this.$store.dispatch("activeInboxLeadItem/tearDown");
      this.$store.dispatch("activeInboxInboundCommItem/tearDown");

      // If no item selected then return to default
      if (!nextItem) {
        this.activeClientId = null;
        this.activeType = null;
        this.loading = false;
      } else {
        // If new lead then set up activeInboxLeadItem
        if (nextItem.inbox_type === ENUMS.InboxTypeEnum.NewLead.value) {
          this.activeType = ENUMS.InboxTypeEnum.NewLead.value;

          this.$store.dispatch("activeInboxLeadItem/setup", nextItem.id).then(() => {
            this.loading = false;
          });
        }

        // If inbound comm then set up activeInboxInboundCommItem
        if (nextItem.inbox_type === ENUMS.InboxTypeEnum.InboundComm.value) {
          this.activeType = ENUMS.InboxTypeEnum.InboundComm.value;

          this.$store.dispatch("activeInboxInboundCommItem/setup", nextItem.id).then(() => {
            this.loading = false;
          });
        }

        // Join the viewedByInbox channel
        this.viewedBy = []; // Need to clear viewedBy before getting new viewedBy info
        const viewedBy = joinPresenceChannel("viewedByInbox", nextItem.client_id);
        const authUserId = this.$store.getters.authUser.id;

        viewedBy.here((users) => {
          this.viewedBy = users.filter(user => user.id !== authUserId);
        })
          .joining((user) => {
            if (user.id !== authUserId) {
              this.viewedBy.push(user);
            }
          })
          .leaving((user) => {
            this.viewedBy = this.viewedBy.filter(viewed => {
              return viewed.id !== user.id;
            });
          });

        // Set activeClientId
        this.activeClientId = nextItem.client_id;
      }
    },
    resetFilters() {
      this.$store.commit("inbox/resetFilters", "inbox")
    },
    changePreset(preset) {
      if (! preset) {
        this.resetFilters()
        return
      }

      for (const [filterKey, filterValue] of Object.entries(preset.filters)) {
        this.$store.commit("inbox/setOneFilter", ["inbox", filterKey, filterValue])
      }
    },
    resetActivePresetTab() {
      this.currentPresetTab = 0
      this.activePresetId = 0
    },
    handlePresetTabChange(tabIndex) {
      if (tabIndex === 0) {
        this.resetActivePresetTab()
        return
      }

      this.activePresetId = this.inboxPresets[tabIndex - 1].id
    },
    managePreset() {
      this.resetActivePresetTab()
      this.$refs.manageInboxPresetsModal.open()
    },
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.inbox_layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  grid-template-rows: 48px  minmax(0, 1fr);
  gap: $defaultGap $defaultGap;
  grid-template-areas:
    "filters filters"
    "menu content";
  padding: 0 $pagePaddingHorizontal $defaultGap;
  height: 100%;

  @media screen and (min-width: 1400px) {
    grid-template-columns: 340px 1fr;
  }

  @media screen and (min-width: 1600px) {
    grid-template-columns: 400px 1fr;
  }
}

.inbox_layout--filters {
  grid-area: filters;
  width: 100%;
  padding: 11px 14px 0;
  background: $white;
}

.inbox_layout--menu {
  grid-area: menu;
  overflow-y: auto;
}

.inbox_layout--content {
  grid-area: content;
}

.inbox_menu--empty {
  padding: 2rem 10px;
  font-style: italic;
  text-align: center;

  &--heading {
    margin-bottom: 6px;
    font-size: 1.125rem;
    color: $text-muted;
  }

  &--message {
    color: $text-light;
    line-height: 1.4;
  }
}

.inbox_content--empty {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.inbox_content--loading--message {
  margin-top: -4rem;
  color: $text-light;
  font-size: 2rem;
  font-style: italic;
}

.inbox_content--empty--message {
  margin-top: -4rem;
  padding: 4rem 6rem;
  border: 1px solid $borderDark;
  color: $text-light;
  font-size: 2rem;
  font-style: italic;
}
</style>
